import AuthStore from 'stores/AuthStore';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import {Set} from 'immutable';
import {UserId} from 'records/User';

export const DashboardTypeEnum = Object.freeze({
  exec: 0,
  manager: 1,
  rep: 2,
  coach: 3,
});
export const dashV2Path = 'dashv2';
export const HISTORY_ACTIONS = Object.freeze({
  PUSH: 'PUSH',
  POP: 'POP',
});
export const TOOLTIP_DELAY = 300;
export const ELLIPSIS_DELAY = 600;

export function getDashboardPath(authUser) {
  const type = getDashboardType(authUser);
  const paths = ['admin', 'manager', 'rep', 'coach'];
  if (type === DashboardTypeEnum.rep) {
    // XXX remove this when rep is in dashv2
    return '/metrics/profile';
  } else {
    return `/${dashV2Path}/${paths[type]}/${authUser.id}`;
  }
}

export function getDashboardType(authUser) {
  if (authUser.permissions.includes('can_view_dept_stats') && authUser.role !== 'user')
    return DashboardTypeEnum.exec;
  if (authUser.isAdmin()) return DashboardTypeEnum.exec;
  if (authUser.isManager() && authUser.isTeamManager) return DashboardTypeEnum.manager;
  if (authUser.isCoach) return DashboardTypeEnum.coach;
  return DashboardTypeEnum.rep;
}

/**
 * Return props.managerId if it is present and is not the same as the loggedIn user
 */
export function getManagerIdIfNotAuthUser(props) {
  const {managerId, coachId} = props;
  const loggedInUser = AuthStore.getState().user;
  if (loggedInUser.isAdmin() && managerId && !coachId) {
    // If an admin has a managerId, then return that managerId even if it is the same as the id of the admin,
    // because it means the admin wants to see her page as her manager role
    return managerId;
  }
  const lowestLevelId = coachId ? coachId : managerId;
  return loggedInUser.id !== lowestLevelId ? lowestLevelId : null;
}

export const ensureReviewMetadata = () => {
  return Promise.all([
    CoachingReviewTypeActions.fetchMeetingCategories(false),
    CoachingReviewTypeActions.fetchAllReviewTypes(false),
  ]);
};

/**
 * Given previous props and current props, check whether an update is necessary
 * startDate, endDate and selectedTeam are the three properties
 * that this function uses to check if update is needed
 * if considerTeam is false, selectedTeam will not be checked
 * if considerDate is false, startDate and endDate will not be checked
 * @param prevProps prev props
 * @param currProps curr props
 * @param considerTeam whether to check selectedTeam changes
 * @param considerDate whether to check start/end dates changes
 * @param considerCoachingPlans whether to check coaching plans changes
 * @returns {boolean} should update or not
 */
export function shouldUpdate(
  prevProps,
  currProps,
  considerTeam = true,
  considerDate = true,
  considerCoachingPlans = false
) {
  if (!considerDate && !considerTeam && !considerCoachingPlans) {
    console.warn('All things not considered, nothing to do');
    return false;
  }
  const {startDate, endDate, selectedTeam, coachingPlansTracker} = currProps;
  const {
    startDate: prevStartDate,
    endDate: prevEndDate,
    selectedTeam: prevSelectedTeam,
    coachingPlansTracker: prevCoachingPlansTracker,
  } = prevProps;
  return (
    (considerDate &&
      (!dateEquals(startDate, prevStartDate) || !dateEquals(endDate, prevEndDate))) ||
    (considerTeam &&
      ((!selectedTeam && prevSelectedTeam) ||
        (selectedTeam && !prevSelectedTeam) ||
        (selectedTeam && prevSelectedTeam && selectedTeam.id !== prevSelectedTeam.id))) ||
    (considerCoachingPlans && coachingPlansTracker !== prevCoachingPlansTracker)
  );
}

/**
 * Check if two nullable momentjs dates have the same day (ignore time i.e.)
 */
function dateEquals(dateA, dateB) {
  if (dateA == null && dateB == null) {
    return true;
  }
  if ((dateA == null && dateB != null) || (dateA != null && dateB == null)) {
    return false;
  }
  return dateA.isSame(dateB, 'day');
}

/**
 * Returns a moment date object that is the startDate given the endDate, if default period is used
 * @param endDateExclusive, moment date object, no cloning needed, this function will take care of that
 */
export function toDefaultPeriodBackInTime(endDateExclusive) {
  return endDateExclusive.clone().subtract(1, 'month');
}

export const noCoachingAgendaPages = Set(['coaching-plans-for-you']);

export interface DashUserParams {
  repId?: UserId | null;
  coachId?: UserId | null;
  managerId?: UserId | null;
  adminId?: UserId | null;
}

export function isViewingOwnDash({coachId, managerId, adminId}: DashUserParams): boolean {
  const authId = AuthStore.getState().user?.id;

  switch (true) {
    case adminId && !managerId && !coachId: // Admin viewing own admin dash
    case adminId === managerId: // ----------- Admin viewing own manager dash
    case managerId === authId && !coachId: //  Manager viewing own dash
    case coachId === authId: // -------------- Coach viewing own dash
      return true;
    default:
      return false;
  }
}

/**
 * The type of dashboard being viewed, regardless who of the user is
 */
export enum DashViewState {
  ADMIN,
  MANAGER,
  COACH,
}

/**
 * Return the type of dashboard being viewed
 */
export function getDashViewState({coachId, managerId}: DashUserParams) {
  if (coachId) return DashViewState.COACH;
  if (managerId) return DashViewState.MANAGER;
  return DashViewState.ADMIN;
}

/**
 * Return whether a specific dash type is being viewed
 */
export function isDashViewState(params: DashUserParams, state: DashViewState): boolean {
  return getDashViewState(params) === state;
}

/**
 * Determine whether dashboard sidebar should show
 * @param params
 */
export function shouldRenderSidebar(params: DashUserParams) {
  const pageParts = location.pathname.split('/');
  const page = pageParts[pageParts.length - 1];

  // Nobody renders sidebar on these pages
  if (noCoachingAgendaPages.contains(page)) return false;

  // Admins/Managers render their own summary sidebar
  const onSummary = page === 'summary';
  if (onSummary && !isDashViewState(params, DashViewState.COACH)) return false;

  // Otherwise, render sidebar
  return true;
}
